import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { StyledGridDiv } from 'components/shared/FormGroup/GridFormGroup';
import { FC, ReactNode } from 'react';
import type { ProductGraph, ProductNode } from 'state/product-description/model';
import type { BatchWorkspaceVersion, QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';
import type { WorkspaceType } from 'state/workspaces/model';

import { useBlockOrFieldType } from '../../hooks/useBlockOrFieldType';
import { useFieldOrBlockHidden } from '../../hooks/useFieldOrBlockHidden';
import { useFieldMaxWidth, useFieldMinWidth } from '../../hooks/useFieldWidth';
import { useProductView } from '../../hooks/useProductView';
import { componentForType } from './FormBuilder.helper';
import { GenericErrorBoundary } from './GenericErrorBoundary';

interface FormBuilderProps {
  tabId: string;
  rootNodes: ProductGraph;
  workspaceType: WorkspaceType;
  quoteWorkspaceVersion: QuoteWorkspaceVersion;
  batchWorkspaceVersion: BatchWorkspaceVersion;
  mustForceAdvancedMode: boolean;
  batchTabId?: string;
}

export const FormBuilder: FC<FormBuilderProps> = ({ rootNodes, ...tabProps }) => (
  <>
    {rootNodes.map(node => (
      <RenderFormNodeComponent {...tabProps} node={node} level={1} key={node.id} />
    ))}
  </>
);

interface RenderFormNodeComponentProps {
  level: number;
  node: ProductNode;
  tabId: string;
  workspaceType: WorkspaceType;
  quoteWorkspaceVersion: QuoteWorkspaceVersion;
  batchWorkspaceVersion: BatchWorkspaceVersion;
  mustForceAdvancedMode: boolean;
  isMultipleApply?: boolean;
  labelContent?: ReactNode;
  tabIndex?: number;
  fieldNodeList?: ReadonlyArray<ProductNode>;
  batchTabId?: string;
  batchColumnIndex?: number;
}

export const RenderFormNodeComponent: FC<RenderFormNodeComponentProps> = ({
  tabId,
  node,
  level,
  workspaceType,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
  isMultipleApply = false,
  tabIndex,
  fieldNodeList,
  batchTabId,
  batchColumnIndex,
}) => {
  const metadataType = useBlockOrFieldType(node.id, node.children);

  const productView = useProductView();
  const type = node.type ?? metadataType;

  const fieldMetadata = useFieldMetadata(node.id);
  const NodeComponent = componentForType(type, productView, isMultipleApply, fieldMetadata);

  const isHidden = useFieldOrBlockHidden(
    node.id,
    workspaceType,
    quoteWorkspaceVersion,
    batchWorkspaceVersion,
    mustForceAdvancedMode,
  );
  const minWidth = useFieldMinWidth(node.id);
  const maxWidth = useFieldMaxWidth(node.id);
  if (isHidden || NodeComponent === null) {
    return <HiddenField workspaceType={workspaceType} minWidth={minWidth} maxWidth={maxWidth} />;
  }
  return (
    <GenericErrorBoundary
      key={`poor-man-error-boundary-form-builder-${node.id}`}
      context={{ nodeId: node?.id, nodeType: node?.type, metadataType, productView }}
    >
      <NodeComponent
        tabId={tabId}
        tabIndex={tabIndex}
        node={node}
        level={level}
        key={`form-builder-${node.id}`}
        workspaceType={workspaceType}
        quoteWorkspaceVersion={quoteWorkspaceVersion}
        batchWorkspaceVersion={batchWorkspaceVersion}
        mustForceAdvancedMode={mustForceAdvancedMode}
        fieldNodeList={fieldNodeList}
        batchTabId={batchTabId}
        batchColumnIndex={batchColumnIndex}
      />
    </GenericErrorBoundary>
  );
};

interface HiddenFieldProps {
  workspaceType: WorkspaceType | undefined;
  minWidth: number;
  maxWidth: number;
}

function HiddenField({ workspaceType, minWidth, maxWidth }: HiddenFieldProps) {
  if (workspaceType === 'newBatch') {
    return <div className="field-hidden " />;
  }
  if (workspaceType === 'addonManagement') {
    return <StyledGridDiv $minWidth={minWidth} $maxWidth={maxWidth} className="field-hidden" />;
  }
  return null;
}

RenderFormNodeComponent.displayName = 'RenderFormNodeComponent';
